'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Link } from '@/components/Link';
import { MAGAZINE_LABEL_CLASS_NAME } from '@/components/magazine/magazineLabelClassName';
import type { MagazineArticleTeaser } from '@/lib/api';
import { useMagazineHref } from '@/lib/magazine/useMagazineHref';
import { imageAlt } from '@/lib/render/imageAlt';
import { buildImageUrl } from '@/lib/render/imageUrl';

/**
 * One card per article in the grid. Not editor-placeable — the Magazine
 * Article List renders it — so it has no CMS schema and no registry entry.
 *
 * Two variants, driven by the data: with a `heroImage` the image fills the
 * card top and the badge sits on its lower-left edge; without, the whole card
 * is sand with the badge on top. The card is never white.
 */
export const ArticleTeaserCard = ({
  article,
}: {
  article: MagazineArticleTeaser;
}) => {
  const t = useTranslations('magazine');
  const magazineHref = useMagazineHref();
  const { title, intro, slug, category, heroImage, readTimeMinutes } = article;
  const imageSrc = heroImage ? buildImageUrl(heroImage) : undefined;
  // The article title stands in for a hero image an editor has not named in
  // the media library.
  // Undefined on a brand that serves no article route: the card then renders
  // its content unlinked rather than with an empty `href` (spec 10.01.02 § 2.4).
  const href = magazineHref('article', slug);

  const badge = category ? (
    // The design fixes the badge label to #002B60 across all brands. The only
    // theme token with that exact value is `--color-price`, which is scoped to
    // campaign price figures, so the value is written out here rather than
    // borrowing an unrelated semantic token.
    <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-[#002B60] uppercase">
      {category.title}
    </span>
  ) : null;

  const cardClassName =
    'bg-secondary-lighter group flex h-full flex-col overflow-hidden rounded-2xl';

  const content = (
    <>
      {imageSrc ? (
        <div className="relative aspect-video w-full">
          <Image
            src={imageSrc}
            alt={imageAlt(heroImage, title)}
            fill
            sizes="(min-width: 1024px) 33vw, 100vw"
            className="object-cover"
          />
          {badge && <div className="absolute bottom-4 left-4">{badge}</div>}
        </div>
      ) : (
        badge && <div className="px-4 pt-4">{badge}</div>
      )}

      <div className="flex flex-1 flex-col gap-1 p-4">
        <h3 className="line-clamp-2 text-lg font-semibold">{title}</h3>
        {readTimeMinutes != null && (
          <p className="text-primary text-sm font-medium">
            {t('readTime', { minutes: readTimeMinutes })}
          </p>
        )}

        <p
          className={classNames(
            'text-sm',
            imageSrc ? 'line-clamp-4' : 'line-clamp-12',
          )}
        >
          {intro}
        </p>
        <span className={classNames('mt-auto pt-2', MAGAZINE_LABEL_CLASS_NAME)}>
          {t('readMore')}
        </span>
      </div>
    </>
  );

  // One anchor around the whole card: a single tab stop, one destination.
  return href ? (
    <Link href={href} className={cardClassName}>
      {content}
    </Link>
  ) : (
    <div className={cardClassName}>{content}</div>
  );
};
